<template>
    <v-container>
        <!-- SETS -->
        <v-row align="center" justify="start" v-if="sets.length > 0" class="board-container">
            <v-col cols="12" v-for="(set, i) in sets" :key="i">
                <v-row align="center">
                    <v-col cols="3" class="caption pr-0" @click="updateSet(set, i)">
                        {{ $tc('exercises.sets', 1) }} {{ Number(i) + 1 }}
                    </v-col>
                    <v-col :cols="readonly ? 10 : 7" align="center" @click="updateSet(set, i)">
                        <v-row v-for="(data, j) in set.data" :key="j" no-gutters align="center">
                            <v-col cols="8" class="text-end text-truncate subtitle-2">
                                {{ data.name }}
                            </v-col>
                            <v-col cols="2" class="text-center">
                                <v-icon small>{{ data.icon }}</v-icon>
                            </v-col>
                            <v-col cols="2" class="text-start subtitle-2">
                                {{ data.value }}
                            </v-col>
                        </v-row>
                    </v-col>
                    <v-col v-if="!readonly" cols="2" class="pa-0" align="right">
                        <v-btn small icon @click.stop="duplicate(set, i)" class="mr-1 mb-1">
                            <v-icon>$copy</v-icon>
                        </v-btn>
                        <v-btn small icon @click.stop="deleteSet(set, i)" class="mr-1 mb-1">
                            <v-icon>$delete</v-icon>
                        </v-btn>
                    </v-col>
                    <v-divider v-if="sets.length > i + 1" />
                </v-row>
            </v-col>
        </v-row>

        <!-- ADD SET -->
        <v-row v-if="!readonly">
            <v-col cols="12" align="center">
                <v-btn outlined small @click.stop="createSet" class="normal-btn mr-1">
                    <v-icon left>mdi-plus</v-icon>
                    {{ $t('exercises.add_set') }}
                </v-btn>
                <v-btn outlined small @click.stop="$emit('hide')" class="normal-btn mr-1">
                    <v-icon>mdi-chevron-up</v-icon>
                </v-btn>
                <v-btn outlined small @click.stop="$emit('click:rest')" class="normal-btn mr-1">
                    {{ hasTime ? $t('common.edit') : $t('common.add') }} {{ $t('exercises.break') }}
                    <v-icon right>mdi-plus</v-icon>
                </v-btn>
            </v-col>
        </v-row>

        <!-- DIALOG SETS -->
        <v-dialog v-if="!readonly" v-model="dialog" :fullscreen="isMobile" persistent no-click-animation
            max-width="500px" transition="dialog-bottom-transition">
            <v-card>
                <v-card-title class="kimsa-elevation">
                    {{ editMode ? $t('exercises.edit_set') : $t('exercises.add_set') }}
                    <v-spacer></v-spacer>
                    <v-icon color="secondary" @click="cancelSet">$close</v-icon>
                </v-card-title>
                <v-card-text class="pt-3">
                    <v-row dense>
                        <!-- REPS/TIME -->
                        <v-col :cols="isFirstSet ? 6 : 12" :md="isFirstSet ? 8 : 12">
                            <v-text-field solo flat outlined dense
                                :type="repsType && repsType.value === 'reps' ? 'text' : 'number'"
                                :inputmode="repsType && repsType.value === 'reps' ? 'text' : 'numeric'" min="0"
                                @keypress="repsValidation" color="secondary"
                                :prepend-inner-icon="repsType && icons[repsType.value] || icons.reps"
                                :suffix="!isFirstSet && repsType ? repsType.name : ''" v-model="reps" hide-details>
                            </v-text-field>
                        </v-col>
                        <v-col v-if="isFirstSet" cols="6" md="4">
                            <kimsa-combobox outlined dense :placeholder="$t('common.select_placeholder')"
                                :items="repsOptions" @change="repsType = $event" :value="repsType" ref="repsType" />
                        </v-col>
                        <!-- RPE/PERCENTAGE/RIR -->
                        <v-col :cols="isFirstSet ? 6 : 12" :md="isFirstSet ? 8 : 12" v-if="rpeType">
                            <v-text-field solo flat outlined dense type="number" inputmode="numeric" min="0"
                                :max="maxValueRpeType" @blur="maxValue('rpeType', maxValueRpeType())"
                                @keypress="isNumber" color="secondary"
                                :prepend-inner-icon="rpeType && icons[rpeType.value] || icons.rpe"
                                :suffix="!isFirstSet && rpeType ? rpeType.name : ''" v-model="rpe" hide-details />
                        </v-col>
                        <v-col v-if="isFirstSet && rpeType" cols="6" md="4">
                            <kimsa-combobox outlined dense :placeholder="$t('common.select_placeholder')"
                                :items="rpeOptions" @change="rpeType = $event" :value="rpeType" ref="rpeType" />
                        </v-col>
                        <!-- TEMPO/ADDITIONAL -->
                        <v-col :cols="isFirstSet ? 6 : 12" :md="isFirstSet ? 8 : 12" v-if="tempoType">
                            <v-text-field solo flat outlined dense type="number" inputmode="numeric" min="0"
                                :maxlength="tempoType && tempoType.value === 'tempo' ? undefined : 3"
                                @keypress="tempoValidation" color="secondary"
                                :prepend-inner-icon="tempoType && icons[tempoType.value] || icons.tempo"
                                :suffix="!isFirstSet && tempoType ? tempoType.name : ''" v-model="tempo" hide-details />
                        </v-col>
                        <v-col v-if="isFirstSet && tempoType" cols="6" md="4">
                            <kimsa-combobox outlined dense :placeholder="$t('common.select_placeholder')"
                                :items="tempoOptions" @change="tempoType = $event" :value="tempoType" ref="tempoType" />
                        </v-col>
                        <!-- "WEIGHT" -->
                        <v-col cols="12" v-if="hasWeight">
                            <v-text-field solo flat outlined dense type="number" inputmode="numeric" min="0"
                                @keypress="isNumber" color="secondary" :prepend-inner-icon="icons.weight"
                                :suffix="$t('exercises.weight')" v-model="weight" hide-details />
                        </v-col>
                        <!-- INTENSITY -->
                        <v-col :cols="isFirstSet ? 6 : 12" :md="isFirstSet ? 8 : 12" v-if="intensityType">
                            <v-text-field solo flat outlined dense @keypress="intensityValidation" color="secondary"
                                :prepend-inner-icon="intensityType && icons[intensityType.value] || icons.beat_min"
                                :suffix="!isFirstSet && intensityType ? intensityType.name : ''" v-model="intensity"
                                hide-details />
                        </v-col>
                        <v-col v-if="isFirstSet && intensityType" cols="6" md="4">
                            <kimsa-combobox outlined dense :placeholder="$t('common.select_placeholder')"
                                :items="intensityOptions" @change="intensityType = $event" :value="intensityType"
                                ref="intensityType" />
                        </v-col>
                        <!-- MICROPAUSE -->
                        <v-col cols="12" v-if="hasMicroPause">
                            <v-text-field solo flat outlined dense min="0" @keypress="isNumber" color="secondary"
                                :prepend-inner-icon="icons.micropause" :suffix="$t('exercises.micropause')"
                                v-model="micropause" hide-details />
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn @click="cancelSet" small depressed class="normal-btn">
                        {{ $t('common.cancel') }}
                    </v-btn>
                    <v-btn :disabled="!isSetValid" @click="saveSet" small depressed color="primary"
                        class="black--text normal-btn">
                        {{ $t('common.save') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>

import { mapState } from 'vuex'
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";

export default {
    name: "SimpleBoard",
    components: { KimsaCombobox },
    props: {
        readonly: {
            type: Boolean,
            default: false
        },
        defaultConfig: {
            type: Object,
            default: null
        },
        categoryExercise: {
            type: String,
            default: null,
        },
        hasTime: {
            type: Boolean,
            default: false
        },
    },
    mounted() {
        const vm = this
        vm.setDefaults()
    },
    data(vm) {
        return {
            sets: [],
            dialog: false,
            editMode: false,
            index: null,
            repsType: null,
            rpeType: null,
            tempoType: null,
            hasWeight: false,
            intensityType: null,
            reps: 1,
            rpe: 0,
            tempo: 0,
            weight: 0,
            intensity: 0,
            micropause: 0,
            rpeOptions: [
                { name: vm.$t('exercises.rpe'), value: 'rpe' },
                { name: vm.$t('exercises.percentage'), value: 'percentage' },
                { name: vm.$t('exercises.rir'), value: 'rir' },
                { name: vm.$t('exercises.meter_seconds'), value: 'meter_seconds' },
            ],
            tempoOptions: [
                { name: vm.$t('exercises.tempo'), value: 'tempo' },
                { name: vm.$t('exercises.additional'), value: 'additional' }
            ],
            intensityOptions: [
                { name: vm.$t('exercises.beat_min'), value: 'beat_min' },
                { name: vm.$t('exercises.time_km'), value: 'time_km' },
                { name: vm.$t('exercises.km'), value: 'km' },
                { name: vm.$t('exercises.rpe'), value: 'rpe' },
                { name: vm.$t('exercises.time'), value: 'time' },
            ],
            icons: {
                reps: 'mdi-repeat',
                time: 'mdi-av-timer',
                minutes: 'mdi-clock-outline',
                distance: 'mdi-diameter-outline',
                weight: 'mdi-weight',
                rpe: 'mdi-chart-box-outline',
                km: 'mdi-speedometer',
                percentage: 'mdi-percent-outline',
                rir: 'mdi-cached',
                meter_seconds: 'mdi-diameter',
                tempo: 'mdi-timer-outline',
                additional: 'mdi-alarm-plus',
                intensity: 'mdi-chart-timeline-variant-shimmer',
                micropause: 'mdi-timer-off-outline',
                beat_min: 'mdi-heart-pulse',
                time_km: 'mdi-gauge',
            },
        }
    },
    computed: {
        ...mapState(['dialogActive']),
        repsOptions(vm = this) {
            if (!vm.hasIntensity) {
                return [
                    { name: vm.$t('exercises.reps'), value: 'reps' },
                    { name: vm.$t('exercises.time'), value: 'time' },
                    { name: vm.$t('exercises.distance'), value: 'distance' }
                ]
            } else {
                return [
                    { name: vm.$t('exercises.reps'), value: 'reps' },
                    { name: vm.$t('exercises.minutes'), value: 'minutes' },
                    { name: vm.$t('exercises.distance'), value: 'distance' }
                ]
            }
        },
        isSetValid() {
            const vm = this
            if (vm.repsType && !vm.reps) return false
            // if (vm.rpeType && !vm.rpe) return false
            // if (vm.tempoType && !vm.tempo) return false
            // if (vm.hasWeight && !vm.weight) return false
            // if (vm.hasMicroPause && !vm.micropause) return false
            return true
        },
        isFirstSet(vm = this) {
            if (vm.sets.length === 0) return true
            return vm.index === 0
        },
        isStrongCategory(vm = this) {
            return vm.categoryExercise === vm.$t('objectives.strong')
        },
        hasMicroPause(vm = this) {
            return vm.categoryExercise !== vm.$t('objectives.stability') || vm.categoryExercise !== vm.$t('objectives.mobility')
        },
        hasIntensity(vm = this) {
            return vm.categoryExercise === vm.$t('objectives.speed') || vm.categoryExercise === vm.$t('objectives.resistance')
        },
    },
    methods: {
        setDefaults() {
            const vm = this
            if (vm.defaultConfig && vm.defaultConfig.sets && vm.defaultConfig.sets.length > 0) {
                vm.sets = JSON.parse(JSON.stringify(vm.defaultConfig.sets))
                let repsType = null
                if (vm.hasIntensity) {
                    repsType = vm.sets[0] && vm.sets[0].data.find(data =>
                        data.name === vm.$t('exercises.reps') || data.name === vm.$t('exercises.minutes') || data.name === vm.$t('exercises.distance'))
                } else {
                    repsType = vm.sets[0] && vm.sets[0].data.find(data =>
                        data.name === vm.$t('exercises.reps') || data.name === vm.$t('exercises.time') ||
                        data.name === vm.$t('exercises.distance'))
                }
                vm.repsType = repsType && vm.repsOptions.find(data => data.name === repsType.name) || null
                let rpeType = vm.isStrongCategory && vm.sets[0] && vm.sets[0].data.find(data =>
                    data.name === vm.$t('exercises.rpe') || data.name === vm.$t('exercises.percentage') ||
                    data.name === vm.$t('exercises.rir') || data.name === vm.$t('exercises.meter_seconds'))
                vm.rpeType = rpeType && vm.rpeOptions.find(data => data.name === rpeType.name) || null
                let tempoType = vm.sets[0] && vm.sets[0].data.find(data => data.name === vm.$t('exercises.tempo') || data.name === vm.$t('exercises.additional'))
                vm.tempoType = tempoType && vm.tempoOptions.find(data => data.name === tempoType.name) || null
                if (vm.sets[0] && vm.sets[0].data.find(data => data.name === vm.$t('exercises.weight'))) vm.hasWeight = true
                let intensityType = vm.hasIntensity && vm.sets[0] && vm.sets[0].data.find(data => data.name === vm.$t('exercises.beat_min') || data.name === vm.$t('exercises.time_km') ||
                    data.name === vm.$t('exercises.km') || data.name === vm.$t('exercises.rpe' || data.name === vm.$t('exercises.time')))
                vm.intensityType = intensityType && vm.intensityOptions.find(data => data.name === intensityType.name) || null
            } else {
                vm.repsType = vm.repsOptions[0]
                if (vm.isStrongCategory) {
                    vm.rpeType = vm.rpeOptions[0]
                    vm.tempoType = vm.tempoOptions[0]
                    vm.hasWeight = true
                }
                if (vm.hasIntensity) {
                    vm.intensityType = vm.intensityOptions[0]
                }
            }
        },
        createSet() {
            const vm = this
            vm.dialog = true
            vm.setDialogActive(true)
        },
        cancelSet() {
            const vm = this
            vm.dialog = false
            vm.setDialogActive(false)
            vm.reset()
        },
        updateSet(set, index) {
            const vm = this
            vm.editMode = true
            vm.index = index

            let reps = null
            if (vm.hasIntensity) {
                reps = set.data.find(data =>
                    data.name === vm.$t('exercises.reps') || data.name === vm.$t('exercises.minutes') || data.name === vm.$t('exercises.distance'))
            } else {
                reps = set.data.find(data =>
                    data.name === vm.$t('exercises.reps') || data.name === vm.$t('exercises.time') ||
                    data.name === vm.$t('exercises.distance'))
            }
            vm.repsType = reps && vm.repsOptions.find(data => data.name === reps.name) || null
            vm.reps = reps && reps.value || 1
            let rpe = vm.isStrongCategory && set.data.find(data =>
                data.name === vm.$t('exercises.rpe') || data.name === vm.$t('exercises.percentage') ||
                data.name === vm.$t('exercises.rir') || data.name === vm.$t('exercises.meter_seconds'))
            vm.rpeType = rpe && vm.rpeOptions.find(data => data.name === rpe.name) || null
            vm.rpe = rpe && rpe.value || 0
            let tempo = set.data.find(data => data.name === vm.$t('exercises.tempo') || data.name === vm.$t('exercises.additional'))
            vm.tempoType = tempo && vm.tempoOptions.find(data => data.name === tempo.name) || null
            vm.tempo = tempo && tempo.value || 0
            let weight = set.data.find(data => data.name === vm.$t('exercises.weight'))
            vm.hasWeight = !!weight
            vm.weight = weight && weight.value || 0
            let intensity = vm.hasIntensity && set.data.find(data => data.name === vm.$t('exercises.beat_min') || data.name === vm.$t('exercises.time_km') ||
                data.name === vm.$t('exercises.km') || data.name === vm.$t('exercises.rpe') || data.name === vm.$t('exercises.time'))
            vm.intensityType = intensity && vm.intensityOptions.find(data => data.name === intensity.name) || null
            vm.intensity = intensity && intensity.value || 0
            let micropause = set.data.find(data => data.name === vm.$t('exercises.micropause'))
            vm.micropause = !!micropause && micropause.value || 0
            vm.createSet()
        },
        saveSet() {
            const vm = this
            let payload = { data: [] }
            let changeReps = false
            let changeWeight = false
            let changeTempo = false
            let changeIntensity = false

            if (vm.sets.length > 0) {
                if (vm.repsType && vm.sets[0].data[0].name !== vm.repsType.name) changeReps = true
                if (vm.rpeType && vm.sets[0].data[1].name !== vm.rpeType.name) changeWeight = true
                if (vm.tempoType && vm.sets[0].data[2].name !== vm.tempoType.name) changeTempo = true
                if (vm.intensityType && vm.sets[0].data[1].name !== vm.intensityType.name) changeIntensity = true
            }

            if (vm.repsType) {
                payload.data.push({
                    name: vm.repsType.name,
                    short_name: `short_${vm.repsType.value}`,
                    value: vm.reps,
                    icon: vm.icons[vm.repsType.value]
                })
            }
            if (vm.rpeType) {
                payload.data.push({
                    name: vm.rpeType.name,
                    short_name: `short_${vm.rpeType.value}`,
                    value: vm.rpe,
                    icon: vm.icons[vm.rpeType.value]
                })
            }
            if (vm.tempoType) {
                payload.data.push({
                    name: vm.tempoType.name,
                    short_name: `short_${vm.tempoType.value}`,
                    value: vm.tempo,
                    icon: vm.icons[vm.tempoType.value]
                })
            }

            if (vm.hasWeight) {
                payload.data.push({
                    name: vm.$t('exercises.weight'),
                    short_name: 'short_weight',
                    value: vm.weight,
                    icon: vm.icons.weight
                })
            }

            if (vm.intensityType) {
                payload.data.push({
                    name: vm.intensityType.name,
                    short_name: `short_${vm.intensityType.value}`,
                    value: vm.intensity,
                    icon: vm.icons[vm.intensityType.value]
                })
            }
            if (vm.hasMicroPause) {
                payload.data.push({
                    name: vm.$t('exercises.micropause'),
                    short_name: 'short_micropause',
                    value: vm.micropause,
                    icon: vm.icons.micropause
                })
            }

            // console.log('payload', payload)
            vm.editMode ? vm.sets.splice(vm.index, 1, payload) : vm.sets.push(payload)

            if (vm.sets.length > 0 && vm.index === 0 && (changeReps || changeWeight || changeTempo || changeIntensity)) {
                vm.sets.forEach((set, i) => {
                    if (changeReps) {
                        vm.sets[i].data[0].name = vm.repsType.name
                        vm.sets[i].data[0].icon = vm.icons[vm.repsType.value]
                        if (isNaN(vm.sets[i].data[0].value)) {
                            vm.sets[i].data[0].value = 1
                        }
                    }
                    if (changeWeight) {
                        vm.sets[i].data[1].name = vm.rpeType.name
                        vm.sets[i].data[1].icon = vm.icons[vm.rpeType.value]
                    }
                    if (changeTempo) {
                        vm.sets[i].data[2].name = vm.tempoType.name
                        vm.sets[i].data[2].icon = vm.icons[vm.tempoType.value]
                    }
                    if (changeIntensity) {
                        vm.sets[i].data[1].name = vm.intensityType.name
                        vm.sets[i].data[1].icon = vm.icons[vm.intensityType.value]
                    }
                })
            }
            // console.log('vm.sets', vm.sets)

            vm.cancelSet()
        },
        reset() {
            const vm = this
            vm.editMode = false
            vm.index = null
            // vm.repsType = null
            // vm.rpeType = null
            // vm.tempoType = null
            // vm.hasWeight = false
            vm.reps = 1
            vm.weight = 0
            vm.tempo = 0
            vm.intensity = 0
            vm.micropause = 0
            vm.setDefaults()
        },
        duplicate(set, index) {
            const vm = this
            let template = JSON.parse(JSON.stringify(set))
            vm.sets.splice(index, 0, template)
        },
        deleteSet(index) {
            const vm = this
            vm.sets.splice(index, 1)
        },
        isNumber(event) {
            let charCode = event.keyCode;
            if (charCode > 31 && (charCode < 48 || charCode > 57)) {
                event.preventDefault();
            } else {
                return true;
            }
        },
        tempoValidation(event) {
            const vm = this
            let charCode = event.keyCode;
            if (vm.tempoType && vm.tempoType.value === 'tempo') {
                if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 46) {
                    event.preventDefault();
                } else {
                    return true;
                }
            } else {
                if ((charCode < 97 || charCode > 122) && (charCode < 65 || charCode > 90)) {
                    event.preventDefault();
                } else {
                    return true;
                }
            }
        },
        intensityValidation(event) {
            const vm = this
            let charCode = event.keyCode;
            if (
                (charCode < 48 || charCode > 57) // NUMBERS
                && (charCode < 97 || charCode > 122) // LETTERS
                && (charCode < 65 || charCode > 90) // LETTERS
                && charCode !== 47 // SLASH /
            ) {
                event.preventDefault();
            } else {
                return true;
            }
        },
        repsValidation(event) {
            const vm = this
            let charCode = event.keyCode;
            if (vm.tempoType && vm.tempoType.value === 'time') {
                if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 46) {
                    event.preventDefault();
                } else {
                    return true;
                }
            } else {
                if ((charCode < 48 || charCode > 57) // JUST NUMBERS
                    && charCode !== 40 && charCode !== 41 // PARENTHESIS
                    && charCode !== 77 && charCode !== 109 // 'M' and 'm'
                    && charCode !== 65 && charCode !== 97  // 'A' and 'a'
                    && charCode !== 88 && charCode !== 120 // 'X' and 'x'
                    && charCode !== 32 // SPACE
                    && charCode !== 58 // ':'
                ) {
                    event.preventDefault();
                } else {
                    return true;
                }
            }
        },
        maxValue(attribute, max) {
            const vm = this
            if (max === undefined) return
            if (vm[attribute] > max) vm[attribute] = max
        },
        maxValueRpeType() {
            const vm = this
            if (vm.rpeType) {
                if (vm.rpeType.value === 'percentage') return 100
                if (vm.rpeType.value === 'rir' || vm.rpeType.value === 'rpe') return 10
            }
            return undefined
        }
    },
    watch: {
        addWeight(val) {
            if (!val) this.newSetKG = 0
        },
        dialogActive(val) {
            if (!val) this.cancelSet()
        },
        sets(val) {
            const vm = this
            // console.log('sets:val', {sets: val})
            vm.$emit('change', { sets: val })
        },
        defaultConfig(val, oldVal) {
            if (JSON.stringify(val) !== JSON.stringify(oldVal)) this.setDefaults()
        }
    }
}
</script>

<style scoped>
.board-container {
    padding: 5px 10px !important;
    border-radius: 5px;
    border: 1px solid #d2d2d2
}

.v-divider {
    margin-right: 40%;
    margin-left: 40%;
    color: #c5c5c5;
}
</style>